import React, { useEffect, useState, useCallback } from 'react';
import { sb } from '../../services/supabase';
import { PageHeader, StatCard, Card, Table, Td, EmptyRow, Badge, CompanyInit } from '../../components/ui';

export default function CompanyDashboard({ companyId }: { companyId: string }) {
  const [stats, setStats] = useState({ total: 0, present: 0, absent: 0, late: 0 });
  const [attendance, setAttendance] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
    const { data: users } = await sb.from('users').select('id').eq('company_id', companyId).eq('is_active', true).in('role', ['employee', 'field_executive', 'manager', 'company_admin']);
    const ids = (users || []).map((u: any) => u.id);
    if (ids.length === 0) { setLoading(false); return; }
    const { data } = await sb.from('attendance')
      .select('*, user:users(full_name, employee_code)')
      .in('user_id', ids).eq('date', dateStr).order('check_in_time', { ascending: false });
    const records = data || [];
    setAttendance(records);
    setStats({
      total: ids.length,
      present: records.filter((r: any) => r.status === 'present').length,
      absent: records.filter((r: any) => r.status === 'absent').length,
      late: records.filter((r: any) => r.status === 'late').length,
    });
    setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Dashboard" subtitle={today} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 32 }}>
        <StatCard label="Total Employees" value={stats.total} color="var(--accent)" />
        <StatCard label="Present Today" value={stats.present} color="var(--accent)" />
        <StatCard label="Late Today" value={stats.late} color="var(--warn)" />
        <StatCard label="Absent Today" value={stats.absent} color="var(--danger)" />
      </div>
      <Card>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Today's Attendance</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{loading ? 'Loading...' : `${attendance.length} records`}</div>
        </div>
        <Table headers={['Employee', 'Code', 'Check In', 'Check Out', 'Status']}>
          {attendance.length === 0 ? <EmptyRow cols={5} message={loading ? "Loading..." : "No attendance records yet today"} /> :
           attendance.map(r => (
            <tr key={r.id}>
              <Td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><CompanyInit name={r.user?.full_name || '?'} size={32} /><div style={{ fontWeight: 600 }}>{r.user?.full_name}</div></div></Td>
              <Td mono>{r.user?.employee_code || '—'}</Td>
              <Td mono>{r.check_in_time ? new Date(r.check_in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</Td>
              <Td mono>{r.check_out_time ? new Date(r.check_out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : <span style={{ color: 'var(--muted)' }}>Still in</span>}</Td>
              <Td><Badge type={r.status === 'present' ? 'green' : r.status === 'late' ? 'yellow' : 'red'}>{r.status}</Badge></Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}
